// react
import { useState, useRef, useEffect, useMemo } from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { default as Grid } from '@mui/material/Grid2';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// tabler icons
import IconLogout from '@tabler/icons-react/dist/esm/icons/IconLogout';
import IconUserCircle from '@tabler/icons-react/dist/esm/icons/IconUserCircle';
import IconUserCog from '@tabler/icons-react/dist/esm/icons/IconUserCog';
import IconPhoto from '@tabler/icons-react/dist/esm/icons/IconPhoto';

// notistack
import { enqueueSnackbar } from 'notistack';

// project constants
import { FILE_SIZE_MAX_BYTES, FILE_SIZE_MAX_WARNING } from '@a1/shared';
import userTypes from 'constants/userTypes';

// project utils
import { setManagerProfilePicture } from 'store/features/userSlice';
import useLogoutAndRedirect from 'hooks/useLogoutAndRedirect';
import getAppVersion from 'utils/app-version';
import { useGetEntityName } from 'utils/entities';
import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import { useNavigate } from 'react-router';
import { useUserProfile } from 'hooks-ts/useUserProfile';
import { base64ToObjectURL } from 'utils-ts/base64';

// project components
import MainCard from 'components-ts/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import CopyToClipboardWithMUI from 'ui-component/CopyToClipBoard';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const customization = useSelector((state) => state.customization);
  const userData = useSelector((state) => state.user?.data);
  const logoutAndRedirect = useLogoutAndRedirect();
  const navigate = useNavigate();
  const { profile, refetch } = useUserProfile();

  const userAccessRole = userData.AccessRoles?.length ? userData.AccessRoles[0].name : 'N/A';
  const userTypeEntityName = useGetEntityName(userData.type === userTypes.ExternalUser ? 'externalUser' : userData?.type);

  const profilePhotoPreview = useMemo(() => {
    if (typeof profile?.profilePhoto === 'string' && profile.profilePhoto.startsWith('data:')) {
      return base64ToObjectURL(profile.profilePhoto);
    }
    return userData?.profilePhoto || undefined;
  }, [profile?.profilePhoto, userData?.profilePhoto]);

  const managerProfilePhoto = profilePhotoPreview || '';

  // User initials used in avatar if no profile photo available
  const userInitials = useMemo(() => {
    let initials = '';
    if (userData?.firstName) {
      initials += String(userData.firstName).slice(0, 1).toUpperCase();
    }
    if (userData?.lastName) {
      initials += String(userData.lastName).slice(0, 1).toUpperCase();
    }
    return initials;
  }, [userData?.firstName, userData?.lastName]);

  // Profile Picture
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [tempPhoto, setTempPhoto] = useState(null);
  const [isExceedPhotoFileSizeLimit, setIsExceedPhotoFileSizeLimit] = useState(false);
  const [isSavingPhoto, setIsSavingPhoto] = useState(false);
  const [isClearingPhoto, setIsClearingPhoto] = useState(false);

  const managerInitialPhotoBlobName = (userData?.profilePhoto || '').split('/').pop() || '';

  const [open, setOpen] = useState(false);

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  const { appVersion, commitHashShort, commitHashComplete } = getAppVersion();

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleChangePhoto = (event) => {
    const photoFile = event.target.files[0];
    setIsExceedPhotoFileSizeLimit(false);
    setTempPhoto(photoFile);
  };

  const handleClosePhotoDialog = () => {
    if (!isSavingPhoto && !isClearingPhoto) {
      setOpenPhotoDialog(false);
      setTempPhoto(null);
    }
  };

  const getNewPhotoURL = async () => {
    if (tempPhoto) {
      const { size } = tempPhoto;
      if (size > FILE_SIZE_MAX_BYTES) {
        setIsExceedPhotoFileSizeLimit(true);
        return null;
      }

      const formData = new FormData();
      formData.append('newPhoto', tempPhoto);

      if (managerInitialPhotoBlobName) {
        formData.append('prevPhotoBlobName', managerInitialPhotoBlobName);
      }

      const response = await callAzureFunction({
        url: `/managers/upload-profile-photo`,
        method: 'post',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    }
    return null;
  };

  const handleSavePhoto = async () => {
    setIsSavingPhoto(true);
    let snackMsg = 'Profile photo has successfully updated';
    let snackVariant = '';
    let shouldShowSnackbar = true;

    try {
      if (tempPhoto) {
        const newPhotoURL = await getNewPhotoURL();
        if (!newPhotoURL) {
          shouldShowSnackbar = false;
          return;
        }

        await callAzureFunction({
          url: `/manager/profile-photo`,
          method: 'put',
          data: { newPhoto: newPhotoURL }
        });

        dispatch(setManagerProfilePicture(newPhotoURL));
        await refetch();
        setTempPhoto(null);
      } else {
        shouldShowSnackbar = false;
      }
      snackVariant = 'success';
    } catch (error) {
      snackMsg = 'Something happened while updating the profile photo';
      snackVariant = 'error';
      handleError(error);
    } finally {
      setIsSavingPhoto(false);
      handleClosePhotoDialog();
      if (shouldShowSnackbar) {
        enqueueSnackbar({
          variant: snackVariant,
          message: <Typography sx={{ maxWidth: 500 }}>{snackMsg}</Typography>,
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
      }
    }
  };

  const handleClearPhoto = async () => {
    setIsClearingPhoto(true);
    let snackMsg = 'Profile photo has successfully cleared';
    let snackVariant = '';

    try {
      if (managerInitialPhotoBlobName) {
        const formData = new FormData();
        formData.append('prevPhotoBlobName', managerInitialPhotoBlobName);
        await callAzureFunction({
          url: `/managers/clear-profile-photo/${userData?.id}`,
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        dispatch(setManagerProfilePicture(null));
        await refetch();
        setTempPhoto(null);
        snackVariant = 'success';
      }
    } catch (error) {
      snackMsg = 'Something happened while clearing profile photo';
      snackVariant = 'error';
      handleError(error);
    } finally {
      setIsClearingPhoto(false);
      handleClosePhotoDialog();
      enqueueSnackbar({
        variant: snackVariant,
        message: <Typography sx={{ maxWidth: 500 }}>{snackMsg}</Typography>,
        anchorOrigin: { vertical: 'top', horizontal: 'center' }
      });
    }
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  useEffect(
    () => () => {
      // Cleanup
      if (profilePhotoPreview && profilePhotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotoPreview);
      }
    },
    [profilePhotoPreview]
  );

  return (
    <>
      <Box ref={anchorRef} style={{ cursor: 'pointer' }} onClick={handleToggle}>
        {profilePhotoPreview ? (
          <Avatar src={profilePhotoPreview} alt="avatar" sx={{ width: 40, height: 40, border: '2px solid', borderColor: 'primary.main' }} />
        ) : (
          <Avatar alt="avatar" sx={{ width: 40, height: 40, border: '2px solid', borderColor: 'primary.main' }}>
            {userInitials}
          </Avatar>
        )}
      </Box>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box sx={{ p: 2 }}>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">Good Day,</Typography>
                        <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                          {userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : ''}
                        </Typography>
                      </Stack>
                      <Typography my={0.5} variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                        {userTypeEntityName || ''} {userData?.type === userTypes.ExternalUser && `| ${userAccessRole}`}
                      </Typography>
                      {appVersion && commitHashShort && (
                        <Stack direction="row" alignItems="center" mt={1}>
                          <Typography variant="subtitle2">
                            v{appVersion} | {commitHashShort}
                          </Typography>
                          {commitHashComplete && (
                            <CopyToClipboardWithMUI
                              textToCopy={commitHashComplete}
                              iconButtonSx={{ fontSize: '12px' }}
                              showSnackbar={false}
                            />
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                  <Grid style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                    <Box sx={{ p: 1 }}>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%'
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5
                          }
                        }}
                      >
                        {/* -- put outlook calendar feature to sleep
                          {userData.type === userTypes.Manager && (
                          <ListItemButton
                            disabled={isLoading}
                            sx={{ borderRadius: `${customization.borderRadius}px` }}
                            onClick={handleAcquireCalendarToken}
                          >
                            <ListItemIcon>
                              <CalendarMonthIcon stroke={1.2} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant="body2">Sync Outlook Calendar Events</Typography>} />
                          </ListItemButton>
                        )} */}
                        {userData.type === userTypes.Manager && (
                          <ListItemButton
                            sx={{ borderRadius: `${customization.borderRadius}px` }}
                            onClick={() => {
                              setOpenPhotoDialog(true);
                              setOpen(false);
                            }}
                          >
                            <ListItemIcon>
                              <IconPhoto stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant="body2">Change Profile Picture</Typography>} />
                          </ListItemButton>
                        )}

                        <ListItemButton
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                          onClick={() => {
                            navigate('/profile-settings');
                            setOpen(false);
                          }}
                        >
                          <ListItemIcon>
                            <IconUserCog stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Profile Settings</Typography>} />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                          onClick={async () => {
                            await logoutAndRedirect();
                          }}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </Grid>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* Change Profile Picture Dialog */}
      <Dialog open={openPhotoDialog} onClose={handleClosePhotoDialog}>
        <DialogContent>
          <Stack direction="row" gap={2} alignItems="center" justifyContent="center">
            {tempPhoto || managerProfilePhoto ? (
              <Box
                component="img"
                src={tempPhoto ? URL.createObjectURL(tempPhoto) : managerProfilePhoto}
                alt="booking-page-logo"
                sx={{ height: 150, width: 150, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <IconUserCircle color="#1ad19e" stroke={1} size={150} />
            )}
            <Button component="label" variant="text" startIcon={<CloudUploadIcon />} size="small">
              <span>
                Upload Photo
                <VisuallyHiddenInput type="file" accept="image/jpeg,image/png" onChange={handleChangePhoto} />
              </span>
            </Button>
          </Stack>
          {isExceedPhotoFileSizeLimit && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {FILE_SIZE_MAX_WARNING}
            </Alert>
          )}
          <Stack direction="row" gap={2} mt={4}>
            <div>
              <Button disabled={isSavingPhoto || isClearingPhoto} onClick={handleClosePhotoDialog} fullWidth>
                Close
              </Button>
            </div>
            <div>
              <Button
                disabled={isSavingPhoto || !managerInitialPhotoBlobName}
                loading={isClearingPhoto}
                color="error"
                variant="contained"
                onClick={handleClearPhoto}
                fullWidth
              >
                Clear
              </Button>
            </div>
            <div>
              <Button disabled={isClearingPhoto} loading={isSavingPhoto} variant="contained" onClick={handleSavePhoto} fullWidth>
                Update Photo
              </Button>
            </div>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileSection;
